import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useThree } from '@react-three/fiber';
import { useTexture } from '@react-three/drei';
import * as THREE from 'three';
import { GLOBE_RADIUS, earthMeshRef } from './geo';

/** Realistic Earth rendered UNLIT — the satellite imagery already contains its
 *  own lighting; relighting it crushes oceans and blows out snow. A 2K map
 *  paints instantly, then 8K (desktop) / 4K (mobile) streams in. */
export function Earth({ onHiRes }: { onHiRes?: () => void }) {
  const gl = useThree((s) => s.gl);
  const invalidate = useThree((s) => s.invalidate);
  const base = useTexture('/textures/earth-2k.webp'); // instant (Suspense)
  const [hi, setHi] = useState<THREE.Texture | null>(null);

  // Kept in a ref so it never re-triggers the texture-loading effect.
  const onHiResRef = useRef(onHiRes);
  onHiResRef.current = onHiRes;

  const prepare = useCallback(
    (t: THREE.Texture) => {
      t.colorSpace = THREE.SRGBColorSpace;
      t.anisotropy = Math.min(16, gl.capabilities.getMaxAnisotropy());
      t.minFilter = THREE.LinearMipmapLinearFilter;
      t.magFilter = THREE.LinearFilter;
      t.generateMipmaps = true;
      t.needsUpdate = true;
    },
    [gl],
  );

  useLayoutEffect(() => prepare(base), [base, prepare]);

  // Progressive upgrade: 2K → 4K (mobile) / 8K (desktop) → 16K (capable desktop).
  // The 16K tier (~0.7 GB VRAM) is gated behind a fine pointer + a large max
  // texture size so phones/weak GPUs stay on 8K/4K.
  useEffect(() => {
    let cancelled = false;
    const coarse = typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches;
    const loader = new THREE.TextureLoader();
    const midUrl = coarse ? '/textures/earth-4k.webp' : '/textures/earth-8k.webp';
    loader
      .loadAsync(midUrl)
      .then((t) => {
        if (cancelled) {
          t.dispose();
          return;
        }
        prepare(t);
        setHi(t);
        invalidate();
        onHiResRef.current?.();

        if (!coarse && gl.capabilities.maxTextureSize >= 16384) {
          loader
            .loadAsync('/textures/earth-16k.webp')
            .then((t2) => {
              if (cancelled) {
                t2.dispose();
                return;
              }
              prepare(t2);
              setHi(t2); // the previous (8K) texture is disposed by the [hi] cleanup
              invalidate();
            })
            .catch((err: unknown) => console.error('Earth 16K texture failed:', err));
        }
      })
      .catch((err: unknown) => console.error('Earth texture upgrade failed:', err));
    return () => {
      cancelled = true;
    };
  }, [prepare, invalidate, gl]);

  // Dispose the manually-loaded hi-res texture on unmount (base is cache-owned).
  useEffect(() => () => hi?.dispose(), [hi]);

  const map = hi ?? base;

  return (
    <mesh
      ref={(m) => {
        earthMeshRef.current = m;
      }}
    >
      <sphereGeometry args={[GLOBE_RADIUS, 128, 128]} />
      <meshBasicMaterial map={map} toneMapped={false} />
    </mesh>
  );
}
